"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import type { PageTheme } from "@/lib/services/lead-service"
import { TextoDaPagina } from "./pecas-da-pagina"

/*
  ─── O bloco de HTML do personal, numa caixa separada da página ───

  O HTML que o personal cola é dele, e a página em que ele aparece é do app:
  `/p/<apelido>` e `/produto/<slug>` moram na mesma origem em que o personal e o
  aluno estão logados, com o token no `localStorage` e sem CSP. Por isso ele
  passa por três barreiras, e cada uma sozinha já segura o script:

  1. O DOMPurify (aqui, na página) tira script, `on*=`, `javascript:`, iframe,
     `<link>`, `<meta>` e `<base>`.
  2. O que sobra é desenhado num iframe `sandbox` SEM `allow-same-origin`. O
     documento da caixa tem origem opaca: nada que rode lá dentro alcança o
     login, o `localStorage`, os cookies ou o resto da página. O CSS do personal
     também fica lá dentro, e não muda os outros blocos nem lê o que a pessoa
     digita no formulário da página.
  3. A caixa (`public/caixa-do-html.html`) tem Content-Security-Policy que só
     deixa rodar o script dela, pelo hash. Mexeu nesse script, refaça o hash:
     `node scripts/hash-da-caixa-do-html.mjs`.

  NUNCA junte `allow-scripts` com `allow-same-origin` neste `sandbox`: com os
  dois, a caixa volta a ter a origem do app e pode tirar o próprio sandbox. O
  `allow-scripts` existe só para o script da caixa, que mede a altura e passa os
  cliques em âncora. Formulário não envia (sem `allow-forms`), e link só leva a
  página para outro endereço com o clique da pessoa.

  A caixa é um arquivo do app e não um `srcdoc` porque o WebView da casca
  (`react-native-webview`, `originWhitelist` http/https) barra o `about:srcdoc`
  no iPhone, e o bloco ficaria em branco dentro do app. O HTML chega por
  `postMessage`, já limpo, e cada edição troca o conteúdo sem recarregar a caixa.

  O que a caixa repete da página, para o HTML sair como saía quando morava nela:
  o reset do Tailwind e as regras de elemento do `globals.css` (estão no CSS da
  caixa); a cor, a fonte, o tamanho e o espaçamento do bloco (lidos aqui, do
  `getComputedStyle`); o `rem` (a fonte da raiz, que no app muda com a largura da
  tela); e as unidades de tela. `100vh` dentro de uma caixa que cresce com o
  conteúdo mede a própria caixa e cresce para sempre, então `vh` e `vw` viram a
  medida da janela da página (`--wt-vh` e `--wt-vw`). As fontes da página não
  carregam lá dentro (a origem opaca não passa no CORS dos arquivos), e vão de
  cá em binário.

  O que muda de propósito, porque é o isolamento valendo nos dois sentidos: as
  classes do Tailwind do app não alcançam mais o HTML do personal, e `@media` lá
  dentro mede a largura do bloco, e não a da tela. A exceção é o `.container`,
  que a caixa repete do `tailwind.config.ts`: é nome comum em HTML pronto, e uma
  página no ar usava. Conferido em 25/10/2026 nos 8 blocos que estavam no ar,
  desenhando cada um do jeito antigo e na caixa: 7 com a mesma altura, e 1 (com
  `@media (min-width:720px)`) mais curto no computador, porque passou a usar o
  layout que cabe na coluna.
*/

const ORIGEM = "wt-bloco-html"
const ENDERECO_DA_CAIXA = "/caixa-do-html.html"
/** Sem `allow-same-origin`, de propósito: ver o comentário acima. */
const SANDBOX = "allow-scripts allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
const ALTURA_MAXIMA = 100_000
/** No HTML que o DOMPurify devolve, o que não aparece na tela: estilo, título e comentário. */
const SO_ESTILO_OU_NOTA = /<style\b[^>]*>[\s\S]*?<\/style>|<title\b[^>]*>[\s\S]*?<\/title>|<!--[\s\S]*?-->/gi

interface Aparencia {
  cor: string
  fonte: string
  fonteTitulo: string
  tamanho: string
  entrelinha: string
  espacamento: string
  borda: string
  esquema: string
}

interface Tela {
  vh: number
  vw: number
  /** A fonte da raiz da página, em px: o `rem` do HTML do personal parte dela. */
  fonteDaRaiz: number
}

interface FonteDaCaixa {
  familia: string
  dados: ArrayBuffer
  descritores: FontFaceDescriptors
}

interface HtmlLimpo {
  html: string
  /** O que vem depois de cada `font-family:` do CSS do personal, cru. */
  familias: string[]
}

/* ─── Limpeza ───

   O DOMPurify só é carregado no NAVEGADOR, depois que o bloco aparece. No
   servidor, o `isomorphic-dompurify` carrega o `jsdom` assim que é importado, e
   o `jsdom` 30 quebra no Node 22.11 da produção (`ERR_REQUIRE_ESM` no
   `html-encoding-sniffer`): importado no topo, ele derrubava o desenho no
   servidor de toda tela com estes blocos. O bloco de HTML sai vazio no HTML do
   servidor e se preenche ao abrir.

   A instância é própria (`DOMPurify(window)`), para os ganchos não valerem para
   mais ninguém. */

/* `vh`, `vw`, `vmin` e `vmax` (e as variantes `s`, `l` e `d`) fora de `url()` e
   de texto entre aspas. O caractere antes do número tem de ser separador, senão
   o `1vh` de uma classe `.col-1vh` viraria conta. */
const UNIDADE_DE_TELA =
  /(url\([^)]*\)|"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')|(^|[^\w.#-])(-?(?:\d+\.\d+|\.\d+|\d+))[sld]?(vh|vw|vmin|vmax)(?![\w-])/gi

function semUnidadeDeTela(css: string): string {
  return css.replace(UNIDADE_DE_TELA, (_trecho, protegido, antes, numero, unidade) => {
    if (protegido) return protegido
    const vh = `calc(${numero} * var(--wt-vh))`
    const vw = `calc(${numero} * var(--wt-vw))`
    switch (String(unidade).toLowerCase()) {
      case "vh":
        return `${antes}${vh}`
      case "vw":
        return `${antes}${vw}`
      case "vmin":
        return `${antes}min(${vw}, ${vh})`
      default:
        return `${antes}max(${vw}, ${vh})`
    }
  })
}

let limpador: Promise<(html: string) => HtmlLimpo> | null = null

function carregarLimpador(): Promise<(html: string) => HtmlLimpo> {
  if (!limpador) {
    limpador = import("isomorphic-dompurify").then(({ default: DOMPurify }) => {
      const purificador = DOMPurify(window)
      let familias: string[] = []
      /* O CSS passa por aqui cru (o texto do <style> e o atributo `style`), que
         é onde dá para ler a fonte sem tropeçar nas aspas do HTML. */
      const prepararCss = (css: string) => {
        for (const citacao of Array.from(css.matchAll(/font-family\s*:\s*([^;}]+)/gi))) familias.push(citacao[1])
        return semUnidadeDeTela(css)
      }
      purificador.addHook("afterSanitizeElements", (no) => {
        if (no.nodeName.toLowerCase() === "style" && no.textContent) {
          no.textContent = prepararCss(no.textContent)
        }
      })
      purificador.addHook("afterSanitizeAttributes", (no) => {
        const estilo = no.getAttribute("style")
        if (estilo) no.setAttribute("style", prepararCss(estilo))
        /* Link leva a página inteira, como levava quando o HTML morava nela. O
           DOMPurify já tira o `target` que vem no código. */
        const nome = no.nodeName.toLowerCase()
        if (nome === "a" || nome === "area") no.setAttribute("target", "_top")
      })
      return (html: string) => {
        familias = []
        /* `FORCE_BODY`: sem ele, o DOMPurify lê o código como documento, o que vem
           antes do primeiro texto (o `<style>` com que quase todo HTML pronto
           começa, ou o `<head>` de uma página inteira colada) cai no head, e ele
           devolve só o body: o estilo sumia sem aviso (BUG-0012, 25/10/2026). A
           caixa recebe o head e o body do que chega, então o estilo vale lá
           dentro, e só lá. */
        const limpo = purificador.sanitize(html, { FORCE_BODY: true })
        return { html: limpo, familias }
      }
    })
  }
  return limpador
}

/* ─── O que a caixa herda da página ─── */

function lerAparencia(bloco: HTMLElement): Aparencia {
  const estilo = getComputedStyle(bloco)
  /* A fonte de título sai de um h2 de verdade, posto e tirado no mesmo instante:
     é o `globals.css` que dá a Rink aos títulos, e a regra pode mudar lá. */
  const sonda = document.createElement("h2")
  sonda.style.cssText = "position:absolute;visibility:hidden;pointer-events:none"
  bloco.appendChild(sonda)
  const fonteTitulo = getComputedStyle(sonda).fontFamily
  sonda.remove()
  /* Tamanho em `rem` e espaçamento em `em`, e não em pixels: a raiz da página
     muda com a largura da tela (`globals.css`), e a caixa acompanha pela
     `fonteDaRaiz` da tela sem precisar ler tudo de novo. */
  const raiz = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
  const tamanho = parseFloat(estilo.fontSize) || raiz
  const entrelinha = parseFloat(estilo.lineHeight)
  const espacamento = parseFloat(estilo.letterSpacing)
  const proporcao = (valor: number) => String(Math.round(valor * 10000) / 10000)
  return {
    cor: estilo.color,
    fonte: estilo.fontFamily,
    fonteTitulo,
    tamanho: `${proporcao(tamanho / raiz)}rem`,
    /* Sem unidade, como no `body`: em pixels, um título maior herdaria a
       entrelinha do texto corrido. */
    entrelinha: Number.isFinite(entrelinha) ? proporcao(entrelinha / tamanho) : "normal",
    espacamento: Number.isFinite(espacamento) ? `${proporcao(espacamento / tamanho)}em` : "normal",
    borda: estilo.borderTopColor,
    /* A mesma da página: com esquema diferente, o navegador pinta o fundo da
       caixa de branco ou de preto, e ela deixa de ser transparente. */
    esquema: estilo.colorScheme || "normal",
  }
}

function medirTela(): Tela {
  const sonda = document.createElement("div")
  sonda.style.cssText = "position:fixed;left:0;top:0;width:100vw;height:100vh;visibility:hidden;pointer-events:none"
  document.body.appendChild(sonda)
  const { width, height } = sonda.getBoundingClientRect()
  sonda.remove()
  const fonteDaRaiz = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
  return { vh: Math.round(height) / 100, vw: Math.round(width) / 100, fonteDaRaiz }
}

function semAspas(nome: string): string {
  return nome.trim().replace(/^["']|["']$/g, "")
}

/** As famílias que a caixa pode pedir: as da página e as que o CSS do personal cita. */
function familiasCitadas(limpo: HtmlLimpo, aparencia: Aparencia): string[] {
  const pilhas = [aparencia.fonte, aparencia.fonteTitulo, ...limpo.familias]
  const familias = new Set<string>()
  for (const pilha of pilhas) {
    for (const nome of pilha.split(",")) {
      const familia = semAspas(nome).toLowerCase()
      if (familia) familias.add(familia)
    }
  }
  return Array.from(familias).sort()
}

const arquivosDeFonte = new Map<string, Promise<ArrayBuffer | null>>()

function baixarFonte(endereco: string): Promise<ArrayBuffer | null> {
  let pedido = arquivosDeFonte.get(endereco)
  if (!pedido) {
    pedido = fetch(endereco)
      .then((resposta) => (resposta.ok ? resposta.arrayBuffer() : null))
      .catch(() => null)
    arquivosDeFonte.set(endereco, pedido)
  }
  return pedido
}

/* As `@font-face` da página (as do `next/font` e as do `globals.css`) cujo nome
   está entre as famílias pedidas. O arquivo vem do cache do navegador, que já
   baixou a fonte para desenhar a página. */
async function fontesParaACaixa(familias: string[]): Promise<FonteDaCaixa[]> {
  const pedidas = new Set(familias)
  const pedidos: Promise<FonteDaCaixa | null>[] = []
  for (const folha of Array.from(document.styleSheets)) {
    let regras: CSSRuleList
    try {
      regras = folha.cssRules
    } catch {
      continue // folha de outra origem
    }
    for (const regra of Array.from(regras)) {
      if (!(regra instanceof CSSFontFaceRule)) continue
      const familia = semAspas(regra.style.getPropertyValue("font-family"))
      if (!pedidas.has(familia.toLowerCase())) continue
      const arquivo = regra.style.getPropertyValue("src").match(/url\(\s*["']?([^"')]+)["']?\s*\)/)
      if (!arquivo) continue
      let endereco: URL
      try {
        endereco = new URL(arquivo[1], folha.href ?? window.location.href)
      } catch {
        continue
      }
      if (endereco.origin !== window.location.origin) continue
      const descritores: FontFaceDescriptors = { display: "swap" }
      const peso = regra.style.getPropertyValue("font-weight")
      const forma = regra.style.getPropertyValue("font-style")
      const faixa = regra.style.getPropertyValue("unicode-range")
      if (peso) descritores.weight = peso
      if (forma) descritores.style = forma
      if (faixa) descritores.unicodeRange = faixa
      pedidos.push(baixarFonte(endereco.href).then((dados) => (dados ? { familia, dados, descritores } : null)))
    }
  }
  return (await Promise.all(pedidos)).filter((fonte): fonte is FonteDaCaixa => fonte !== null)
}

/* ─── O bloco ─── */

export function CaixaDoHtml({ html, theme, isLive }: { html: string; theme: PageTheme; isLive?: boolean }) {
  const blocoRef = useRef<HTMLDivElement>(null)
  const caixaRef = useRef<HTMLIFrameElement>(null)
  const marcaRef = useRef<HTMLSpanElement>(null)
  const telaRef = useRef<Tela | null>(null)
  const [limpo, setLimpo] = useState<HtmlLimpo | null>(null)
  const [aparencia, setAparencia] = useState<Aparencia | null>(null)
  /** Sobe a cada "pronto" da caixa: é quando ela pode receber o conteúdo. */
  const [conversa, setConversa] = useState(0)
  const [altura, setAltura] = useState(0)

  useEffect(() => {
    let valendo = true
    carregarLimpador().then((limpar) => {
      if (valendo) setLimpo(limpar(html))
    })
    return () => {
      valendo = false
    }
  }, [html])

  useEffect(() => {
    if (!blocoRef.current) return
    if (!telaRef.current) telaRef.current = medirTela()
    setAparencia(lerAparencia(blocoRef.current))
  }, [theme.foreground, theme.font_body, theme.font_heading])

  /* O que a caixa pede. Só vale a mensagem que vem DESTA caixa: o app recebe
     mensagem de vídeo do YouTube e da casca pela mesma janela. */
  useEffect(() => {
    function aoOuvir(evento: MessageEvent) {
      const caixa = caixaRef.current
      if (!caixa || evento.source !== caixa.contentWindow) return
      const dados = evento.data
      if (!dados || typeof dados !== "object" || dados.origem !== ORIGEM) return
      if (dados.tipo === "pronto") {
        setConversa((n) => n + 1)
      } else if (dados.tipo === "altura" && Number.isFinite(dados.altura)) {
        setAltura(Math.min(ALTURA_MAXIMA, Math.max(0, Math.ceil(dados.altura))))
      } else if (dados.tipo === "rolar" && Number.isFinite(dados.topo)) {
        /* Um pedaço de dentro da caixa: a marca vai para a mesma altura, e
           quem rola até ela é o navegador, seja a janela ou o painel do editor. */
        const marca = marcaRef.current
        if (!marca) return
        marca.style.top = `${caixa.offsetTop + Math.max(0, dados.topo)}px`
        marca.scrollIntoView({ behavior: "smooth", block: "start" })
      } else if (dados.tipo === "ancora" && typeof dados.id === "string" && dados.id.length <= 200) {
        /* Um bloco da página, como o #checkout. */
        document.getElementById(dados.id)?.scrollIntoView({ behavior: "smooth" })
      }
    }
    window.addEventListener("message", aoOuvir)
    return () => window.removeEventListener("message", aoOuvir)
  }, [])

  useEffect(() => {
    const caixa = caixaRef.current?.contentWindow
    if (!conversa || !caixa || !limpo || !aparencia) return
    caixa.postMessage(
      { origem: ORIGEM, tipo: "conteudo", html: limpo.html, aparencia, tela: telaRef.current, endereco: window.location.href },
      "*",
    )
  }, [conversa, limpo, aparencia])

  const familias = useMemo(() => (limpo && aparencia ? familiasCitadas(limpo, aparencia).join(",") : ""), [limpo, aparencia])

  useEffect(() => {
    if (!conversa || !familias) return
    let valendo = true
    fontesParaACaixa(familias.split(",")).then((fontes) => {
      const caixa = caixaRef.current?.contentWindow
      if (valendo && caixa && fontes.length > 0) caixa.postMessage({ origem: ORIGEM, tipo: "fontes", fontes }, "*")
    })
    return () => {
      valendo = false
    }
  }, [conversa, familias])

  /* A janela mudou de tamanho: o `vh`, o `vw` e o `rem` de dentro acompanham. */
  useEffect(() => {
    let quadro = 0
    function aoMudar() {
      cancelAnimationFrame(quadro)
      quadro = requestAnimationFrame(() => {
        const nova = medirTela()
        const antiga = telaRef.current
        if (antiga && antiga.vh === nova.vh && antiga.vw === nova.vw && antiga.fonteDaRaiz === nova.fonteDaRaiz) return
        telaRef.current = nova
        caixaRef.current?.contentWindow?.postMessage({ origem: ORIGEM, tipo: "tela", ...nova }, "*")
      })
    }
    window.addEventListener("resize", aoMudar)
    return () => {
      cancelAnimationFrame(quadro)
      window.removeEventListener("resize", aoMudar)
    }
  }, [])

  /* Tudo o que foi colado saiu na limpeza (um script sozinho, um iframe): no
     editor, o bloco diz por que está vazio; na página no ar, não aparece nada.
     Estilo, título e comentário sozinhos também não desenham nada: o estilo só
     vale dentro da caixa, e não há o que pintar nela. */
  const vazio = limpo !== null && !limpo.html.replace(SO_ESTILO_OU_NOTA, "").trim()

  return (
    <div ref={blocoRef} className="relative px-4 py-5" style={{ color: theme.foreground }}>
      {aparencia && !vazio && (
        <iframe
          ref={caixaRef}
          src={ENDERECO_DA_CAIXA}
          sandbox={SANDBOX}
          title="Conteúdo em HTML desta página"
          className="block w-full border-0"
          style={{ height: altura, background: "transparent" }}
        />
      )}
      {vazio && !isLive && (
        <TextoDaPagina theme={theme} papel="apoio">
          Nada deste código aparece na página. Script, formulário e vídeo de outro site não funcionam neste bloco.
        </TextoDaPagina>
      )}
      <span ref={marcaRef} aria-hidden className="pointer-events-none absolute left-0 top-0 h-px w-px" />
    </div>
  )
}
